import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, X, Briefcase, Euro } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { v: "new_inquiry",    l: "New inquiry",   color: "text-zinc-400 border-zinc-700" },
  { v: "qualified",      l: "Qualified",     color: "text-blue-400 border-blue-700" },
  { v: "terms_defined",  l: "Terms defined", color: "text-cyan-400 border-cyan-700" },
  { v: "negotiation",    l: "Negotiation",   color: "text-yellow-400 border-yellow-700" },
  { v: "confirmed",      l: "Confirmed",     color: "text-[color:var(--gio-secondary)] border-[color:var(--gio-secondary)]" },
  { v: "production",     l: "Production",    color: "text-orange-400 border-orange-700" },
  { v: "scheduled",      l: "Scheduled",     color: "text-purple-400 border-purple-700" },
  { v: "completed",      l: "Completed",     color: "text-green-400 border-green-700" },
  { v: "closed_lost",    l: "Closed / Lost", color: "text-zinc-600 border-zinc-800" },
];

const OPEN_STAGES = ["new_inquiry","qualified","terms_defined","negotiation","confirmed","production","scheduled"];
const STALE_DAYS_BY_STAGE = { negotiation: 14, confirmed: 21, terms_defined: 21, production: 14, scheduled: 7 };
const MS_PER_DAY = 86400000;

function isStale(deal) {
  if (!OPEN_STAGES.includes(deal.stage)) return false;
  const threshold = STALE_DAYS_BY_STAGE[deal.stage] || 30;
  const ts = Date.parse(deal.updated_at || deal.created_at || "");
  if (!ts) return false;
  return (Date.now() - ts) / MS_PER_DAY >= threshold;
}

function daysSince(deal) {
  const ts = Date.parse(deal.updated_at || deal.created_at || "");
  if (!ts) return 0;
  return Math.floor((Date.now() - ts) / MS_PER_DAY);
}

const EMPTY = {
  brand_name: "", stage: "new_inquiry", website: "", contact_person: "", contact_email: "",
  handle: "", product: "", platform: "", content_type: "", deal_value: 0,
  compensation_type: "Unknown", deliverables_count: 1, due_date: "", notes: "",
};

export default function Pipeline() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    await api.post("/api/pipeline/import-submissions").catch(() => {});
    const [l, s] = await Promise.all([api.get("/api/pipeline/"), api.get("/api/pipeline/summary")]);
    setItems(l.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (d) => { setEditing(d.id); setForm({ ...EMPTY, ...d, due_date: d.due_date || "" }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.brand_name.trim()) return toast.error("Brand name required");
    try {
      const body = { ...form, deal_value: parseFloat(form.deal_value) || 0, deliverables_count: parseInt(form.deliverables_count) || 1 };
      if (editing === "new") await api.post("/api/pipeline/", body);
      else await api.put(`/api/pipeline/${editing}`, body);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const moveStage = async (id, stage) => {
    await api.put(`/api/pipeline/${id}`, { stage });
    load();
  };

  const columns = useMemo(() => {
    const by = {};
    STAGES.forEach(s => by[s.v] = []);
    items.forEach(d => { if (by[d.stage]) by[d.stage].push(d); else by.new_inquiry.push(d); });
    return by;
  }, [items]);

  return (
    <AppLayout title="Brand pipeline" overline="170 · COLLAB DEALS · KANBAN">
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 fade-up" data-testid="pipe-summary">
        <Stat label="Total deals" value={summary?.total ?? 0} testid="pipe-total" />
        <Stat label="Pipeline value" value={`€${(summary?.pipeline_value_eur ?? 0).toFixed(0)}`} accent testid="pipe-value" />
        <Stat label="Closed (completed)" value={`€${(summary?.closed_value_eur ?? 0).toFixed(0)}`} testid="pipe-closed" />
        <Stat label="Confirmed" value={summary?.by_stage?.confirmed ?? 0} testid="pipe-confirmed" />
      </section>

      <div className="flex justify-end mb-4">
        <button onClick={openNew} data-testid="pipe-add-button" className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> New deal</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="pipe-board">
        {STAGES.map((s) => (
          <div key={s.v} className="border border-zinc-900 bg-zinc-950 p-3 min-h-[200px]" data-testid={`pipe-col-${s.v}`}>
            <div className={`overline flex justify-between items-center pb-2 mb-3 border-b ${s.color}`}>
              <span>{s.l}</span>
              <span className="font-mono-ui">{(columns[s.v] || []).length}</span>
            </div>
            <div className="space-y-2">
              {(columns[s.v] || []).map((d) => {
                const stale = isStale(d);
                return (
                <div key={d.id} onClick={() => openEdit(d)} data-testid={`pipe-card-${d.id}`}
                  className={`bg-black border p-3 cursor-pointer transition ${stale ? "border-[color:var(--gio-primary)] shadow-[0_0_0_1px_rgba(255,51,0,0.3)]" : "border-zinc-800 hover:border-[color:var(--gio-primary)]"}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-white font-medium text-sm truncate flex-1">{d.brand_name}</div>
                    {stale && <span className="text-[9px] font-mono-ui uppercase text-[color:var(--gio-primary)] shrink-0" title={`Stale — ${daysSince(d)} days in ${s.l.toLowerCase()}`}>●STALE</span>}
                  </div>
                  {d.product && <div className="text-zinc-400 text-[11px] mt-1 line-clamp-2">{d.product}</div>}
                  <div className="flex items-center justify-between mt-2 text-[10px] font-mono-ui uppercase">
                    <span className="text-zinc-500">{d.platform || "—"} · {daysSince(d)}d</span>
                    {d.deal_value > 0 && <span className="text-[color:var(--gio-secondary)] flex items-center gap-1"><Euro size={10} /> {d.deal_value.toFixed(0)}</span>}
                  </div>
                </div>
                );
              })}
              {(columns[s.v] || []).length === 0 && <div className="text-zinc-700 text-xs text-center py-4">—</div>}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-4" onClick={close} data-testid="pipe-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-900 flex items-center justify-between">
              <div>
                <div className="overline text-[color:var(--gio-primary)]">{editing === "new" ? "NEW DEAL" : "EDIT DEAL"}</div>
                <h2 className="font-heading text-2xl mt-1">{form.brand_name || "Untitled"}</h2>
              </div>
              <button onClick={close} data-testid="pipe-modal-close" className="text-zinc-600 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <F l="Brand Name *"><input data-testid="pipe-f-brand" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="gf-input" /></F>
              <div className="grid sm:grid-cols-2 gap-4">
                <F l="Stage">
                  <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="pipe-f-stage" className="gf-input">
                    {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </F>
                <F l="Website"><input data-testid="pipe-f-web" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className="gf-input" /></F>
                <F l="Contact Person"><input data-testid="pipe-f-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="gf-input" /></F>
                <F l="Contact Email"><input data-testid="pipe-f-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="gf-input" /></F>
                <F l="X / Instagram Handle"><input data-testid="pipe-f-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="gf-input" /></F>
                <F l="Platform"><input data-testid="pipe-f-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="X / IG / TikTok / OF..." className="gf-input" /></F>
                <F l="Content Type"><input data-testid="pipe-f-ctype" value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} placeholder="Post / Reel / Story..." className="gf-input" /></F>
                <F l="Compensation Type">
                  <select value={form.compensation_type} onChange={(e) => setForm({ ...form, compensation_type: e.target.value })} data-testid="pipe-f-comp" className="gf-input">
                    {["Unknown","Paid","Product","Affiliate","Hybrid"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </F>
                <F l="Deal Value (€)"><input data-testid="pipe-f-value" type="number" step="0.01" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} className="gf-input" /></F>
                <F l="# Deliverables"><input data-testid="pipe-f-deliv" type="number" value={form.deliverables_count} onChange={(e) => setForm({ ...form, deliverables_count: e.target.value })} className="gf-input" /></F>
                <F l="Due Date"><input data-testid="pipe-f-due" type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="gf-input" /></F>
              </div>
              <F l="Product / Offer Description"><textarea data-testid="pipe-f-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} rows={3} className="gf-input" /></F>
              <F l="Internal Notes"><textarea data-testid="pipe-f-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gf-input" /></F>
            </div>
            <div className="p-6 border-t border-zinc-900 flex justify-between gap-3">
              <div className="flex gap-3 ml-auto">
                <button onClick={close} data-testid="pipe-cancel" className="btn-ghost">Cancel</button>
                <button onClick={save} data-testid="pipe-save" className="btn-primary inline-flex items-center gap-2"><Briefcase size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .gf-input { width: 100%; background: #000; border: 1px solid #27272a; color: white; padding: 10px 12px; font-size: 13px; outline: none; font-family: 'Manrope', sans-serif; transition: border-color 0.2s; }
        .gf-input:focus { border-color: var(--gio-primary); }
      `}</style>
    </AppLayout>
  );
}

function F({ l, children }) {
  return (<div><div className="overline mb-2">{l}</div>{children}</div>);
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className={`border bg-zinc-950 p-4 ${accent ? "border-[color:var(--gio-primary)]" : "border-zinc-900"}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${accent ? "text-[color:var(--gio-primary)]" : "text-white"}`}>{value}</div>
    </div>
  );
}
